/** Matches the server cap in agentCallLimits (`AGENT_MAX_HISTORY_MESSAGES`). */
export const MAX_CHAT_AGENT_MESSAGES = 30;

/**
 * Keeps the last `maxMessages` messages of any role. If that window would
 * start on an assistant message, the leading assistant messages are dropped
 * so the payload always starts with a user message.
 */
export const limitMessagesToLastMessages = (
  messages,
  maxMessages = MAX_CHAT_AGENT_MESSAGES
) => {
  if (!Array.isArray(messages) || messages.length === 0) return messages;
  const windowStart = Math.max(0, messages.length - maxMessages);
  let startIndex = windowStart;
  while (
    startIndex < messages.length &&
    messages[startIndex]?.role !== "user"
  ) {
    startIndex += 1;
  }
  if (startIndex >= messages.length) return messages.slice(windowStart);
  return messages.slice(startIndex);
};

/**
 * Builds the { role, content }[] payload for chat agent endpoints from the
 * in-memory messages map (excluding reasoning placeholders).
 */
export const formatMessagesForChatAgent = (
  messages,
  userID,
  ephemeralUserTurn = null
) => {
  let messagesFormatted = Object.keys(messages)
    .sort(
      (a, b) =>
        (messages[a].creationTime || 0) - (messages[b].creationTime || 0)
    )
    .map((key) => {
      const message = messages[key];
      const role = message.userID === userID ? "user" : "assistant";

      if (message.isReasoning) {
        return null;
      }
      const hasOptimizationsOrLearnings =
        (message.optimizations?.length ?? 0) > 0 ||
        (message.learnings?.length ?? 0) > 0;
      const assistantContent =
        role === "assistant" && hasOptimizationsOrLearnings
          ? JSON.stringify({
              answer: message.message,
              optimizations: message.optimizations || [],
              learnings: message.learnings || [],
            })
          : message.completion || message.message;
      return {
        content: role === "user" ? message.message : assistantContent,
        role,
      };
    })
    .filter(Boolean);

  const trimmedEphemeral =
    typeof ephemeralUserTurn === "string" ? ephemeralUserTurn.trim() : "";
  if (trimmedEphemeral) {
    messagesFormatted = [
      ...messagesFormatted,
      { role: "user", content: trimmedEphemeral },
    ];
  }

  return limitMessagesToLastMessages(messagesFormatted);
};
